import dayjs, {Dayjs} from 'dayjs';
import 'dayjs/locale/fr';
import LocalizedFormat from 'dayjs/plugin/localizedFormat';
import Timezone from 'dayjs/plugin/timezone';
import UTC from 'dayjs/plugin/utc';
import RelativeTime from 'dayjs/plugin/relativeTime';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(LocalizedFormat);
dayjs.extend(Timezone);
dayjs.extend(UTC);
dayjs.extend(RelativeTime);
dayjs.extend(customParseFormat);

/** A calendar date without time, such as a birth date ('YYYY-MM-DD'). */
const CALENDAR_DATE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Parse a date to display it in the Paris time zone.
 *
 * dayjs reads a calendar date as local midnight: converting it to Paris would
 * shift it to the previous day on a machine east of Paris (La Réunion,
 * Mayotte...), so it is kept as is.
 */
function parse(date?: string | Date): Dayjs {
    return typeof date === 'string' && CALENDAR_DATE.test(date)
        ? dayjs(date)
        : dayjs(date).tz('Europe/Paris');
}

export default {
    /**
     * Returns a string representation of the date (short format).
     *
     * @param  date - Date to convert.
     * @param format - Override format.
     *
     * @return string
     */
    shortFormat(date: string | Date, format = 'DD/MM/YYYY') {
        return parse(date).locale('fr').format(format);
    },

    /**
     * Returns a string representation of the date (medium format).
     *
     * @param  date - Date to convert.
     *
     * @return string
     */
    mediumFormat(date: string | Date) {
        return parse(date).locale('fr').format('DD/MM/YY à LT');
    },

    /**
     * Returns a string representation of the date (long format).
     *
     * @param  {Date|string|undefined} date - Date to convert.
     *
     * @return string
     */
    longFormat(date: string | Date) {
        return parse(date).locale('fr').format('LL à LT');
    },

    /**
     * Returns a string representation of the date (day only, long format).
     *
     * @param  {Date|string|undefined} date - Date to convert.
     *
     * @return string
     */
    dayOnlyLongFormat(date: string | Date) {
        return parse(date).locale('fr').format('LL');
    },

    /**
     * Returns a string representation of the date (day only, long format).
     *
     * @param  {Date|string|undefined} date - Date to convert.
     *
     * @return string
     */
    abbreviatedMonthFormat(date: string | Date) {
        return parse(date).locale('fr').format('DD MMM YYYY');
    },

    /** The birthdate detection custom format */
    detectionFormat(date: string) {
        return parse(date).locale('fr').format('DD-MM-YYYY');
    },

    /**
     * Returns a string representation of relative date.
     *
     * @param  {Date|string|undefined} date - Date to convert.
     *
     * @param withoutSuffix
     * @return string
     */
    relativeFormat(date?: string | Date, withoutSuffix = false) {
        return parse(date).locale('fr').fromNow(withoutSuffix);
    },
    /**
     * Returns a human day and hour representation of the date,
     * e.g. "Aujourd'hui, 10h", "Demain, 9h30", "21 juil., 14h".
     *
     * @param  date - Date to convert.
     *
     * @return string
     */
    humanDayTime(date: string | Date) {
        const parsed = parse(date).locale('fr');
        const time = parsed.format(parsed.minute() === 0 ? 'H[h]' : 'H[h]mm');

        if (parsed.isSame(dayjs(), 'day')) {
            return `Aujourd'hui, ${time}`;
        }

        if (parsed.isSame(dayjs().add(1, 'day'), 'day')) {
            return `Demain, ${time}`;
        }

        return `${parsed.format('D MMM')}, ${time}`;
    },

    /**
     * Compute the age of the patient.
     */
    age(birthdate: string | Date | Dayjs | null | undefined) {
        if (!birthdate) {
            return 'age inconnu';
        }

        const age = dayjs().diff(birthdate, 'year');

        return `${age} ans`;
    }
};
